"use client";

import React, { useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button, Table } from "reactstrap";

import { useAppDispatch, useAppSelector } from "src/hooks/redux-hooks";
import { clearSlideOut } from "src/redux/jobs/slice";
import { fetchFlagSummary } from "src/redux/flag-summary/api";

import { isMakeReadyServiceCategory, updateUrlForOpenJobSidePanel } from 'src/helpers/job-slide-out';
import { createAbortSignal } from "src/helpers/utils";
import useFeatureFlags from "src/hooks/use-feature-flags";

import { PdfIcon } from "src/assets/images/job-details-slide-out/job-details-slide-out";
import FlagActivityIndicator from "src/components/flag-activity-indicator";
import type { IJobs } from "src/types/jobs";

interface JobColumn {
  key: string;
  label: string;
  render: (row: IJobs) => React.ReactNode;
  tableTdClass?: string;
}

const jobStatus: Record<string, string> = {
  created: "awaiting-availability",
  scheduling_in_progress: "scheduling",
  scheduled: "scheduled",
  quoting: "paused",
  quality_check: "paused",
  paused: "paused",
  invoice_ready: "in-progress",
  work_in_progress: "in-progress",
  closed: "in-progress",
  work_completed: "completed",
  canceled: "canceled",
};
const Jobs: React.FC = () => {
  const srId = useAppSelector((state) => state?.jobDetails?.slideOut?.srId);
  const job = useAppSelector((state) => state?.jobDetails?.data);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const jobs = useAppSelector((state) => state?.jobDetails?.data?.jobs);
  const { makeReadySlideout } = useFeatureFlags();
  // Build Back Risk is always-on, so the Activity column + its summary fetch
  // always surface; the indicator shows whatever active flags exist (BBR and/or
  // Escalated).
  const isActivityColumnEnabled = true;

  // Build Back Risk / Escalation: the per-job indicators come from the shared,
  // partner-scoped flag summary (not the job-detail payload). Fetch it once on
  // mount (guarded by the LD flags) so the column is accurate even when the
  // slide-out is opened directly. Fire-and-forget; a failure must not block the
  // jobs list.
  useEffect(() => {
    if (!isActivityColumnEnabled) {
      return undefined;
    }
    const controller = createAbortSignal();
    (dispatch as (action: unknown) => unknown)(
      fetchFlagSummary(controller.signal)({}),
    );
    return () => {
      controller?.abort();
    };
  }, [isActivityColumnEnabled, dispatch]);

  const jobColumns = useCallback(
    (): JobColumn[] => [
      {
        key: "job_no",
        label: "LULA JOB #",
        render: (row) => (
          <Button
            className="btn-link"
            color="btn-deoco"
            onClick={() =>
              updateUrlForOpenJobSidePanel(
                row?.root_job_no ?? "",
                "jobDetails",
                row?.root_job_id ?? "",
                srId ?? "",
              )
            }
          >
            {row?.root_job_no || ""}
          </Button>
        ),
      },
      {
        key: "service_vertical",
        label: "SERVICE VERTICAL",
        render: (row) => row?.service_category || "",
      },
      {
        key: "status",
        label: "STATUS",
        render: (row) => (
          <div
            className={`status ${jobStatus?.[row?.status?.slug || ""] || ""}`}
          >
            {row?.status?.label || ""}
          </div>
        ),
      },
      {
        key: "invoice",
        label: "INVOICE #",
        render: (row) =>
          Array.isArray(row?.invoices)
            ? row?.invoices?.map((item) => (
                <div className="invoice-wrapper" key={item?.invoice_no}>
                  {item?.invoice_no || ""}
                  {item?.share_url ? (
                    <Button
                      type="button"
                      className="pdf-btn"
                      color="btn-deoco"
                      onClick={() => {
                        if (item?.share_url) {
                          window.open(item?.share_url, "_blank");
                        }
                      }}
                    >
                      <PdfIcon />
                      PDF
                    </Button>
                  ) : (
                    ""
                  )}
                </div>
              ))
            : "",
        tableTdClass: "job-invoice",
      },
      ...(isActivityColumnEnabled
        ? [
            {
              // Activity flag mirrors the All-Jobs page via the shared indicator.
              // Driven off the same `/api/flags/summary` indicators map (active
              // Build Back Risk + Escalated), keyed by work_order_job_id — here
              // the row's root_job_id, the job identifier this slide-out
              // displays/navigates by.
              key: "activity",
              label: "Activity",
              tableTdClass: "job-flag-col",
              render: (row: IJobs) => (
                <FlagActivityIndicator jobId={Number(row?.root_job_id)} />
              ),
            },
          ]
        : []),
    ],
    [srId, isActivityColumnEnabled],
  );

  const redirectToPage = useCallback(
    (path: string) => {
      const jobNo = job?.service_request_no;
      if (jobNo) {
        dispatch(clearSlideOut());
        router.push(`${path}?job_no=${jobNo}`);
      }
    },
    [dispatch, job?.service_request_no, router],
  );

  return (
    <React.Fragment>
      <Table responsive className="job-content-table">
        <thead>
          <tr>
            {jobColumns().map((column) => (
              <th key={column?.key}>{column?.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array?.isArray(jobs)
            ? jobs?.map((rowData) => (
                <tr key={rowData?.job_id}>
                  {jobColumns()?.map((column) => (
                    <td
                      key={`${column?.key}`}
                      className={column?.tableTdClass || ""}
                    >
                      {column?.render(rowData)}
                    </td>
                  ))}
                </tr>
              ))
            : ""}
        </tbody>
      </Table>
      {!(makeReadySlideout && isMakeReadyServiceCategory(job?.type))
      && (job?.closed_jobs_count || job?.open_jobs_count) ? (
        <div className="action-wrapper">
          {job?.open_jobs_count ? (
            <Button
              type="button"
              color="deoco"
              className="grey-outline-btn"
              onClick={() => redirectToPage("/open-jobs")}
            >
              View Open Jobs ({job?.open_jobs_count as number})
            </Button>
          ) : (
            ""
          )}
          {job?.closed_jobs_count ? (
            <Button
              type="button"
              color="deoco"
              className="grey-outline-btn"
              onClick={() => redirectToPage("/jobs")}
            >
              View Jobs ({job?.closed_jobs_count as number})
            </Button>
          ) : (
            ""
          )}
        </div>
      ) : (
        ""
      )}
    </React.Fragment>
  );
};

export default Jobs;
